import { actionToId, replayActions, type ReplayResult } from '@texas42/game-runtime';
import type { SavedMatch } from '@texas42/app-model';
import { resolveReplaySeed, toEngineGameConfig } from './config-mapper.js';

export interface RestoreVerification {
  verified: boolean;
  expectedMarks: [number, number];
  replayedMarks: [number, number];
  actionCount: number;
  errors: string[];
}

export interface RestoredMatch {
  match: SavedMatch;
  replay: ReplayResult;
  verification: RestoreVerification;
}

function marksEqual(a: [number, number], b: [number, number]): boolean {
  return a[0] === b[0] && a[1] === b[1];
}

function actionHistoriesEqual(saved: string[], replayed: string[]): boolean {
  return saved.length === replayed.length && saved.every((id, index) => id === replayed[index]);
}

/**
 * Replay a saved match through the authoritative runtime and verify summary metadata.
 */
export function restoreSavedMatch(match: SavedMatch): RestoredMatch {
  const config = toEngineGameConfig(match.config);
  const seed = resolveReplaySeed(match.config);

  const replay = replayActions(seed, match.actionIds, config);
  const replayedMarks: [number, number] = [
    replay.state.teamMarks[0],
    replay.state.teamMarks[1],
  ];
  const replayedActionIds = replay.state.actionHistory.map(actionToId);

  const errors: string[] = [...(replay.errors ?? [])];

  if (!marksEqual(replayedMarks, match.currentMarks)) {
    errors.push(
      `Marks mismatch after replay: expected [${match.currentMarks.join(', ')}], got [${replayedMarks.join(', ')}]`,
    );
  }

  if (!actionHistoriesEqual(match.actionIds, replayedActionIds)) {
    errors.push(
      `Action history mismatch: saved ${match.actionIds.length}, replayed ${replayedActionIds.length}`,
    );
  }

  return {
    match,
    replay,
    verification: {
      verified: errors.length === 0,
      expectedMarks: match.currentMarks,
      replayedMarks,
      actionCount: replayedActionIds.length,
      errors,
    },
  };
}
